import { List, Record } from 'immutable';
import {
  SavableFilterTypes,
  ValueTypes,
} from '~/ducks/machine-filter-v2/types/filter-value-types';
import type { FilterDefinitionType } from '../data/definitions';
import type { SavedFilterAPIResponse } from '~/ducks/machine-filter-v2/types/api-reponse-types';
import type { RangeType } from '../data/filter-values/RangeFilterRecord';
import ErrorRecord from '~/data/ErrorRecord';
import SelectMachinesFilterDefinitionTypeRecord from '~/ducks/machine-filter-v2/data/definitions/SelectMachinesFilterDefinitionTypeRecord';
import SelectedMachinesFilterRecord from '~/ducks/machine-filter-v2/data/filter-values/SelectedMachinesFilterRecord';
import Logger from '~/utils/logger/logger';
import SavedFilterRecord from '~/ducks/machine-filter-v2/data/SavedFilterRecord';
import {
  convertTemporaryFiltersToDefinitionFilters,
  flattenSections,
} from '~/ducks/machine-filter-v2/utils/filter-utils';
import FilterDefinitionValueRecord from '../data/FilterDefinitionValueRecord';
import FilterGroupRecord from '../data/FilterGroupRecord';
import FilterSectionRecord from '../data/FilterSectionRecord';

const keys: Fields = {
  isLoading: false,
  isLoadingSavedFilters: false,
  isSavingFilter: false,
  filterSections: List(),
  activeFilters: new FilterGroupRecord(),
  savedFilters: List(),
  usingSavedFilter: undefined,
  error: undefined,
  savedFiltersError: undefined,
  saveFilterError: undefined,
  showInMapFilter: undefined,
};

interface Fields {
  isLoading: boolean;
  isLoadingSavedFilters: boolean;
  isSavingFilter: boolean;
  filterSections: List<FilterSectionRecord>;
  activeFilters: FilterGroupRecord;
  savedFilters: List<SavedFilterRecord>;
  usingSavedFilter?: string;
  error?: ErrorRecord;
  savedFiltersError?: ErrorRecord;
  saveFilterError?: ErrorRecord;
  showInMapFilter?: SelectedMachinesFilterRecord;
}

export type FilterDefinitionValueTypes =
  | FilterDefinitionValueRecord
  | RangeType
  | string
  | string[];

const showSelectedMachinesFromMapDefinition = new SelectMachinesFilterDefinitionTypeRecord(
  {
    name: {
      name: 'showInMap',
      translationKey: 'web.machine_filter.visibility_filter.selected_from_map',
    },
  }
);

export default class MachineFilterStateRecord extends Record<Fields>(keys) {
  addFilterValue(
    filterDefinition: FilterDefinitionType,
    filter: FilterDefinitionValueTypes
  ): this {
    return this.update('activeFilters', (activeFilters) =>
      activeFilters.addFilterValue(filterDefinition, filter)
    ).clearUsingSavedFilter();
  }

  removeFilterValue(
    filterName: string,
    filterValue?: FilterDefinitionValueRecord
  ): this {
    return this.update('activeFilters', (activeFilters) =>
      activeFilters.removeFilterValue(filterName, filterValue)
    ).clearUsingSavedFilter();
  }

  clearAllFilterValues(): this {
    return this.set(
      'activeFilters',
      new FilterGroupRecord()
    ).clearUsingSavedFilter();
  }

  clearFilterValuesByName(filterName: string): this {
    return this.update('activeFilters', (activeFilters) =>
      activeFilters.clearFilterValuesByName(filterName)
    ).clearUsingSavedFilter();
  }

  setSavedFilters(savedFilters: SavedFilterAPIResponse[]): this {
    return this.set(
      'savedFilters',
      List(
        savedFilters.map((savedFilter) => new SavedFilterRecord(savedFilter))
      )
    );
  }

  getSavedFilters(): List<SavedFilterRecord> {
    return this.savedFilters;
  }

  getFavoriteFilters(): List<SavedFilterRecord> {
    return this.savedFilters.filter((savedFilter) => savedFilter.isFavorite());
  }

  setFilterSections(filterSections: any[]): this {
    const filterSectionList = List(
      filterSections.map(
        (filterSection) => new FilterSectionRecord(filterSection)
      )
    );
    const newFilterList = convertTemporaryFiltersToDefinitionFilters(
      flattenSections(filterSectionList),
      this.getFilters()
    );

    return this.merge({
      filterSections: filterSectionList,
      activeFilters: new FilterGroupRecord({ filters: newFilterList }),
    });
  }

  getFilterSections(): List<FilterSectionRecord> {
    return this.filterSections;
  }

  getFilterValuesByName(filterName: string): ValueTypes | undefined {
    return this.activeFilters.getFilterValuesByName(filterName);
  }

  getFilters(): List<SavableFilterTypes> {
    return this.activeFilters.getFilterValues();
  }

  getShowInMapFilter(): SelectedMachinesFilterRecord | undefined {
    return this.showInMapFilter;
  }

  clearUsingSavedFilter(): this {
    return this.set('usingSavedFilter', undefined);
  }

  getUsingSavedFilter(): string | undefined {
    return this.usingSavedFilter;
  }

  setFilters(filters: List<SavableFilterTypes>): this {
    return this.update('activeFilters', (activeFilters) =>
      activeFilters.setFilterValues(filters)
    ).clearUsingSavedFilter();
  }

  applySavedFilter(savedFilter: SavedFilterRecord): this {
    return this.merge({
      activeFilters: new FilterGroupRecord({
        filters: savedFilter.getFilters(),
      }),
      usingSavedFilter: savedFilter.getId(),
    });
  }

  setSavedFilterIsFavorite(filterId, isFavorite): this {
    return this.editSavedFilter(filterId, (savedFilter: SavedFilterRecord) =>
      savedFilter.set('favorite', isFavorite)
    );
  }

  setSavedFilterIsLoading(filterId, isLoading): this {
    return this.editSavedFilter(filterId, (savedFilter: SavedFilterRecord) =>
      savedFilter.set('isLoading', isLoading)
    );
  }

  editSavedFilter(filterId, editFilterCallback): this {
    const savedFilterIndex = this.savedFilters.findIndex(
      (f) => f.getId() === filterId
    );

    if (savedFilterIndex === -1) {
      Logger.error(
        'Saved filter with id not found in list of saved filters',
        filterId
      );
      return this;
    }

    return this.updateIn(
      ['savedFilters', savedFilterIndex],
      editFilterCallback
    );
  }

  removeSavedFilter(filterId: string): this {
    return this.update('savedFilters', (savedFilters) =>
      savedFilters.filter((f) => f.getId() !== filterId)
    );
  }

  setVisibilityFilter(assetIds: string[]): this {
    return this.set(
      'showInMapFilter',
      new SelectedMachinesFilterRecord({
        filterDefinition: showSelectedMachinesFromMapDefinition,
        assetIds: assetIds,
      })
    );
  }

  clearVisibilityFilter(): this {
    return this.set('showInMapFilter', undefined);
  }
}
